import React, { useState, useEffect, useCallback } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Lock, LogOut, Calendar, Users, Mail, Briefcase, Trash2, Plus, Edit3, Loader2, Check, X, Crown, Music, Mic2, UtensilsCrossed, Store, Camera, Palette, Video, Save } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const TOKEN_KEY = "caf_admin_token";

const SLOT_TYPES = [
  { id: "headliner", label: "Headliner", icon: Crown, adminOnly: true },
  { id: "direct_support", label: "Direct Support", icon: Mic2 },
  { id: "opening_band", label: "Opening Band", icon: Music },
  { id: "food_vendor", label: "Food Vendor", icon: UtensilsCrossed },
  { id: "merch_booth", label: "Merch / Booth", icon: Store },
  { id: "photographer", label: "Photographer", icon: Camera },
  { id: "visual_artist", label: "Visual Artist", icon: Palette },
];

const SLOT_LABEL = Object.fromEntries(SLOT_TYPES.map((t) => [t.id, t.label]));

function authHeaders() {
  return { "X-Admin-Password": localStorage.getItem(TOKEN_KEY) || "" };
}

function LoginGate({ onLogin }) {
  const [pw, setPw] = useState("");
  const [loading, setLoading] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const r = await axios.post(`${API}/admin/login`, { password: pw });
      localStorage.setItem(TOKEN_KEY, r.data.token);
      onLogin();
    } catch (err) {
      toast.error("Invalid password");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center p-5">
      <form onSubmit={submit} data-testid="admin-login-form" className="glass-card p-8 md:p-10 max-w-md w-full border-[#FF00E5]/30">
        <div className="inline-flex items-center justify-center w-14 h-14 border border-[#FF00E5]/50 bg-black/60 glow-magenta mb-5">
          <Lock size={20} className="text-[#FF00E5]" />
        </div>
        <span className="font-mono-display text-[11px] uppercase tracking-[0.35em] text-[#FF00E5]">Admin</span>
        <h1 className="font-display font-black text-3xl text-cream-soft mt-1">C.A.F. CONTROL</h1>
        <p className="mt-2 text-white/55 text-sm">Stephen-only zone.</p>
        <input
          data-testid="admin-password-input"
          type="password"
          required
          value={pw}
          onChange={(e) => setPw(e.target.value)}
          placeholder="Password"
          className="mt-6 w-full bg-black/40 border-b-2 border-white/15 focus:border-[#FF00E5] outline-none px-1 py-3 text-cream-soft placeholder:text-white/30"
        />
        <button
          data-testid="admin-login-submit"
          type="submit"
          disabled={loading || !pw}
          className="mt-6 w-full px-6 py-3.5 bg-[#FF00E5] hover:bg-[#FF00E5]/90 text-black font-display font-bold text-xs uppercase tracking-[0.3em] disabled:opacity-60 flex items-center justify-center gap-2 glow-magenta"
        >
          {loading ? <Loader2 size={14} className="animate-spin" /> : "Enter"}
        </button>
      </form>
    </div>
  );
}

function ShowEditor({ show, onClose, onSaved }) {
  const [savedShow, setSavedShow] = useState(show); // becomes the show after save
  const isNew = !savedShow;
  const [form, setForm] = useState({
    date_label: show?.date_label || "",
    date_iso: show?.date_iso || "",
    city: show?.city || "",
    venue: show?.venue || "",
    ticket_url: show?.ticket_url || "",
    status: show?.status || "upcoming",
    notes: show?.notes || "",
  });
  const [loading, setLoading] = useState(false);
  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const save = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const payload = { ...form, date_iso: form.date_iso || null, ticket_url: form.ticket_url || null, notes: form.notes || null };
      if (!savedShow) {
        const r = await axios.post(`${API}/admin/shows`, payload, { headers: authHeaders() });
        toast.success("Show created — now add performers + slots below.");
        setSavedShow({ ...r.data, slots: [] });
        onSaved();
      } else {
        const r = await axios.put(`${API}/admin/shows/${savedShow.id}`, payload, { headers: authHeaders() });
        toast.success("Show updated");
        setSavedShow({ ...savedShow, ...r.data });
        onSaved();
      }
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Save failed");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="fixed inset-0 z-[200] bg-black/85 backdrop-blur-md overflow-y-auto p-4" onClick={onClose}>
      <form onSubmit={save} onClick={(e) => e.stopPropagation()} className="max-w-2xl mx-auto my-10 glass-card p-8 border-[#FF00E5]/30">
        <div className="flex items-start justify-between mb-6">
          <div>
            <span className="font-mono-display text-[10px] uppercase tracking-[0.3em] text-[#FF00E5]">
              {isNew ? "Create" : savedShow ? "Editing" : "Edit"}
            </span>
            <h3 className="font-display font-black text-2xl text-cream-soft mt-1">
              {isNew ? "NEW SHOW" : savedShow?.venue || "EDIT SHOW"}
            </h3>
          </div>
          <button type="button" onClick={onClose} className="w-9 h-9 border border-white/20 flex items-center justify-center hover:border-[#FF00E5] hover:text-[#FF00E5]"><X size={14} /></button>
        </div>

        <p className="font-mono-display text-[10px] uppercase tracking-[0.3em] text-[#00F0FF] mb-3">// Step 1 · Show details</p>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <Field label="Date Label *" testid="show-date-label" value={form.date_label} onChange={set("date_label")} required placeholder="Aug 14, 2026" />
          <Field label="Date ISO (for sorting)" testid="show-date-iso" type="date" value={form.date_iso} onChange={set("date_iso")} />
          <Field label="City *" testid="show-city" value={form.city} onChange={set("city")} required placeholder="Las Vegas, NV" />
          <Field label="Venue *" testid="show-venue" value={form.venue} onChange={set("venue")} required placeholder="The Underground" />
          <Field label="Ticket URL" testid="show-ticket-url" value={form.ticket_url} onChange={set("ticket_url")} placeholder="https://..." />
          <Select label="Status" testid="show-status" value={form.status} onChange={set("status")} options={[
            ["upcoming", "Upcoming"],
            ["announced", "Announced"],
            ["sold_out", "Sold Out"],
            ["past", "Past"],
          ]} />
        </div>

        <TextArea label="Admin Notes" testid="show-notes" value={form.notes} onChange={set("notes")} placeholder="Internal notes…" />

        <button data-testid="show-save" disabled={loading} type="submit" className="mt-6 px-8 py-3 bg-[#FF00E5] text-black font-display font-bold text-xs uppercase tracking-[0.3em] disabled:opacity-60 hover:bg-[#FF00E5]/90">
          {loading ? "Saving…" : savedShow ? "Save Changes" : "Save · Add Performers Next →"}
        </button>

        {/* Step 2: Performers + vendors (slot manager) — only shown after the show has been saved */}
        {savedShow && (
          <div className="mt-8 pt-7 border-t border-white/15">
            <p className="font-mono-display text-[10px] uppercase tracking-[0.3em] text-[#00F0FF] mb-3">// Step 2 · Performers · Vendors · Open Slots</p>
            <div className="p-4 mb-4 bg-[#FF00E5]/5 border border-[#FF00E5]/20">
              <p className="text-xs text-white/70 font-mono-display uppercase tracking-[0.15em] leading-relaxed">
                <span className="text-[#FF00E5]">Headliner</span> = you assign (band name in the field).<br />
                <span className="text-[#00F0FF]">Opening Band / Direct Support / Vendors</span> = mark them &quot;Open&quot; and bands/vendors can apply via the public site, or you can assign someone directly.
              </p>
            </div>
            <SlotManager show={savedShow} onChanged={onSaved} embedded />
            <button
              type="button"
              onClick={onClose}
              className="mt-6 w-full md:w-auto px-8 py-3 border border-[#00F0FF] text-[#00F0FF] hover:bg-[#00F0FF]/10 font-display font-bold text-xs uppercase tracking-[0.3em] hover:glow-cyan transition-all"
            >
              Done · Close
            </button>
          </div>
        )}
      </form>
    </div>
  );
}

function Field({ label, testid, ...rest }) {
  return (
    <div>
      <label className="block font-mono-display text-[10px] uppercase tracking-[0.3em] text-white/55 mb-1.5">{label}</label>
      <input data-testid={testid} {...rest} className="w-full bg-black/40 border-b-2 border-white/15 focus:border-[#00F0FF] outline-none px-1 py-2.5 text-cream-soft placeholder:text-white/30 text-sm" />
    </div>
  );
}
function Select({ label, testid, options, ...rest }) {
  return (
    <div>
      <label className="block font-mono-display text-[10px] uppercase tracking-[0.3em] text-white/55 mb-1.5">{label}</label>
      <select data-testid={testid} {...rest} className="w-full bg-black/40 border-b-2 border-white/15 focus:border-[#FF00E5] outline-none px-1 py-2.5 text-cream-soft text-sm">
        {options.map(([v, l]) => <option key={v} value={v} className="bg-black">{l}</option>)}
      </select>
    </div>
  );
}
function TextArea({ label, testid, ...rest }) {
  return (
    <div className="mt-4">
      <label className="block font-mono-display text-[10px] uppercase tracking-[0.3em] text-white/55 mb-1.5">{label}</label>
      <textarea rows={3} data-testid={testid} {...rest} className="w-full bg-black/40 border border-white/10 focus:border-[#FF00E5] outline-none px-3 py-2.5 text-cream-soft placeholder:text-white/30 resize-none text-sm" />
    </div>
  );
}

function SlotManager({ show, onChanged, embedded = false }) {
  const [slots, setSlots] = useState(show.slots || []);
  const [adding, setAdding] = useState(embedded); // auto-open the add row inside the editor
  const [newSlot, setNewSlot] = useState({ slot_type: "opening_band", status: "open", assigned_to: "" });

  const refresh = async () => {
    const r = await axios.get(`${API}/shows`);
    const updated = r.data.shows.find(s => s.id === show.id);
    if (updated) setSlots(updated.slots || []);
    onChanged?.();
  };

  const createSlot = async () => {
    try {
      await axios.post(`${API}/admin/shows/${show.id}/slots`, {
        slot_type: newSlot.slot_type,
        status: newSlot.status,
        assigned_to: newSlot.assigned_to || null,
      }, { headers: authHeaders() });
      if (!embedded) setAdding(false);
      setNewSlot({ slot_type: "opening_band", status: "open", assigned_to: "" });
      refresh();
      toast.success("Slot added");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Failed");
    }
  };

  const updateSlot = async (slot, updates) => {
    try {
      await axios.put(`${API}/admin/slots/${slot.id}`, updates, { headers: authHeaders() });
      refresh();
    } catch (err) {
      toast.error("Update failed");
    }
  };

  const deleteSlot = async (slot) => {
    if (!confirm(`Delete this ${SLOT_LABEL[slot.slot_type]} slot?`)) return;
    try {
      await axios.delete(`${API}/admin/slots/${slot.id}`, { headers: authHeaders() });
      refresh();
      toast.success("Slot deleted");
    } catch (err) {
      toast.error("Delete failed");
    }
  };

  return (
    <div className="mt-4 border-t border-white/10 pt-4">
      <div className="flex items-center justify-between mb-3">
        <span className="font-mono-display text-[10px] uppercase tracking-[0.3em] text-white/55">Slots ({slots.length})</span>
        <button onClick={() => setAdding(true)} className="font-mono-display text-[10px] uppercase tracking-[0.25em] text-[#00F0FF] hover:text-glow-cyan flex items-center gap-1">
          <Plus size={11} /> Add slot
        </button>
      </div>

      <div className="space-y-2">
        {slots.map((slot) => {
          const Icon = SLOT_TYPES.find(t => t.id === slot.slot_type)?.icon || Music;
          return (
            <div key={slot.id} className="flex flex-wrap items-center gap-2 p-2.5 bg-black/40 border border-white/10">
              <Icon size={13} className="text-[#00F0FF]" />
              <span className="font-display font-bold text-sm text-cream-soft">{SLOT_LABEL[slot.slot_type]}</span>
              <select
                value={slot.status}
                onChange={(e) => updateSlot(slot, { status: e.target.value })}
                className="bg-black border border-white/15 text-white/85 text-xs px-2 py-1"
              >
                <option value="open">Open</option>
                <option value="pending">Pending</option>
                <option value="booked">Booked</option>
              </select>
              <input
                placeholder="Assigned to (band/vendor)"
                value={slot.assigned_to || ""}
                onChange={(e) => updateSlot(slot, { assigned_to: e.target.value })}
                onBlur={(e) => updateSlot(slot, { assigned_to: e.target.value })}
                className="flex-1 min-w-[120px] bg-black/60 border border-white/15 text-white/85 text-xs px-2 py-1 placeholder:text-white/30"
              />
              <button onClick={() => deleteSlot(slot)} className="text-white/40 hover:text-red-400"><Trash2 size={13} /></button>
            </div>
          );
        })}

        {adding && (
          <div className="flex flex-wrap items-center gap-2 p-2.5 border border-[#FF00E5]/40 bg-[#FF00E5]/5">
            <select value={newSlot.slot_type} onChange={(e) => setNewSlot({ ...newSlot, slot_type: e.target.value })} className="bg-black border border-white/15 text-cream-soft text-xs px-2 py-1">
              {SLOT_TYPES.map(t => <option key={t.id} value={t.id}>{t.label}{t.adminOnly ? " (admin)" : ""}</option>)}
            </select>
            <input placeholder="Assigned to (optional)" value={newSlot.assigned_to} onChange={(e) => setNewSlot({ ...newSlot, assigned_to: e.target.value })} className="flex-1 min-w-[120px] bg-black/60 border border-white/15 text-white/85 text-xs px-2 py-1" />
            <button onClick={createSlot} className="text-[#00F0FF] hover:text-glow-cyan"><Check size={14} /></button>
            <button onClick={() => setAdding(false)} className="text-white/40"><X size={14} /></button>
          </div>
        )}
      </div>
    </div>
  );
}

function ShowsTab() {
  const [shows, setShows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null); // show object or null
  const [creatingNew, setCreatingNew] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await axios.get(`${API}/shows`);
      setShows(r.data.shows || []);
    } catch {
      /* network error — toast handled elsewhere */
    }
    setLoading(false);
  }, []);

  useEffect(() => { load(); }, [load]);

  const deleteShow = async (s) => {
    if (!confirm(`Delete show "${s.venue}" on ${s.date_label}? This removes all slots + applications.`)) return;
    try {
      await axios.delete(`${API}/admin/shows/${s.id}`, { headers: authHeaders() });
      toast.success("Show deleted");
      load();
    } catch {
      toast.error("Delete failed");
    }
  };

  if (loading) return <Loading />;

  return (
    <div>
      <div className="flex items-center justify-between mb-5">
        <h2 className="font-display font-black text-2xl text-cream-soft">Shows · {shows.length}</h2>
        <button data-testid="admin-new-show" onClick={() => setCreatingNew(true)} className="px-4 py-2 bg-[#FF00E5] text-black font-mono-display text-xs uppercase tracking-[0.25em] font-bold hover:bg-[#FF00E5]/90 flex items-center gap-2">
          <Plus size={13} /> New Show
        </button>
      </div>

      <div className="space-y-4">
        {shows.length === 0 && <Empty msg="No shows yet. Click 'New Show' to add the first one." />}
        {shows.map((s) => (
          <div key={s.id} data-testid={`admin-show-${s.id}`} className="glass-card p-5">
            <div className="flex flex-wrap items-start justify-between gap-3">
              <div className="flex-1 min-w-0">
                <div className="font-mono-display text-[10px] uppercase tracking-[0.3em] text-[#00F0FF]">{s.date_label}{s.date_iso ? ` · ${s.date_iso}` : ""}</div>
                <div className="font-display font-bold text-xl text-cream-soft mt-1">{s.venue}</div>
                <div className="text-white/55 text-sm">{s.city} · <span className="text-[#FF00E5]">{s.status}</span> · {s.open_slot_count} slot{s.open_slot_count !== 1 ? "s" : ""} open</div>
                {s.notes && <div className="mt-1 text-white/45 text-xs italic">{s.notes}</div>}
              </div>
              <div className="flex gap-2">
                <button onClick={() => setEditing(s)} className="px-3 py-2 border border-white/20 text-white/70 hover:border-[#00F0FF] hover:text-[#00F0FF] text-xs flex items-center gap-1.5"><Edit3 size={12} /> Edit</button>
                <button onClick={() => deleteShow(s)} className="px-3 py-2 border border-white/20 text-white/70 hover:border-red-400 hover:text-red-400 text-xs flex items-center gap-1.5"><Trash2 size={12} /> Delete</button>
              </div>
            </div>
            <SlotManager show={s} onChanged={load} />
          </div>
        ))}
      </div>

      {creatingNew && <ShowEditor show={null} onClose={() => setCreatingNew(false)} onSaved={load} />}
      {editing && <ShowEditor show={editing} onClose={() => setEditing(null)} onSaved={load} />}
    </div>
  );
}

function TracksTab() {
  const [tracks, setTracks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [savingId, setSavingId] = useState(null);

  const load = async () => {
    setLoading(true);
    try {
      const r = await axios.get(`${API}/tracks`);
      setTracks(r.data.tracks || []);
    } catch {
      toast.error("Failed to load tracks");
    }
    setLoading(false);
  };

  useEffect(() => { load(); }, []);

  const updateLocal = (id, key, value) =>
    setTracks((rows) => rows.map((t) => (t.id === id ? { ...t, [key]: value } : t)));

  const save = async (track) => {
    setSavingId(track.id);
    try {
      await axios.put(`${API}/admin/tracks/${track.id}`, {
        title: track.title,
        tag: track.tag || null,
        youtube_id: track.youtube_id || null,
        bandcamp_url: track.bandcamp_url || null,
        status: track.status,
        order: track.order,
      }, { headers: authHeaders() });
      toast.success(`Saved: ${track.title}`);
    } catch {
      toast.error("Save failed");
    } finally {
      setSavingId(null);
    }
  };

  const addNew = async () => {
    try {
      const r = await axios.post(`${API}/admin/tracks`, {
        title: "New Track",
        status: "coming_soon",
        order: (tracks[tracks.length - 1]?.order || tracks.length) + 1,
      }, { headers: authHeaders() });
      setTracks([...tracks, r.data]);
      toast.success("Track added — edit it below");
    } catch {
      toast.error("Failed to add");
    }
  };

  const remove = async (track) => {
    if (!confirm(`Delete track "${track.title}"?`)) return;
    try {
      await axios.delete(`${API}/admin/tracks/${track.id}`, { headers: authHeaders() });
      setTracks(tracks.filter((t) => t.id !== track.id));
      toast.success("Track deleted");
    } catch {
      toast.error("Delete failed");
    }
  };

  // Accept full YouTube URL or 11-char ID
  const handleYoutubeInput = (id, value) => {
    let videoId = value.trim();
    const match = videoId.match(/(?:youtube\.com\/.*[?&]v=|youtu\.be\/|youtube\.com\/embed\/)([A-Za-z0-9_-]{11})/);
    if (match) videoId = match[1];
    updateLocal(id, "youtube_id", videoId);
  };

  if (loading) return <Loading />;

  return (
    <div>
      <div className="flex items-center justify-between mb-5 flex-wrap gap-3">
        <div>
          <h2 className="font-display font-black text-2xl text-cream-soft">Tracks &amp; Videos · {tracks.length}</h2>
          <p className="font-mono-display text-[10px] uppercase tracking-[0.25em] text-white/55 mt-1">
            Paste a YouTube URL or 11-char ID. Tap Save when done.
          </p>
        </div>
        <button data-testid="admin-add-track" onClick={addNew} className="px-4 py-2 bg-[#FF00E5] text-black font-mono-display text-xs uppercase tracking-[0.25em] font-bold hover:bg-[#FF00E5]/90 flex items-center gap-2">
          <Plus size={13} /> Add Track
        </button>
      </div>

      <div className="space-y-3">
        {tracks.map((t, i) => (
          <div key={t.id} data-testid={`admin-track-${t.id}`} className="glass-card p-5">
            <div className="flex items-start gap-3 mb-4">
              <span className="font-display font-black text-3xl text-gradient-neon select-none w-10 shrink-0">
                {String(i + 1).padStart(2, "0")}
              </span>
              <div className="flex-1 min-w-0">
                <input
                  data-testid={`track-title-${t.id}`}
                  value={t.title}
                  onChange={(e) => updateLocal(t.id, "title", e.target.value)}
                  className="w-full bg-transparent border-0 border-b-2 border-white/15 focus:border-[#FF00E5] outline-none px-1 py-2 text-cream-soft font-display font-bold text-xl"
                  placeholder="Track title"
                />
              </div>
              <button onClick={() => remove(t)} className="text-white/40 hover:text-red-400 p-2" aria-label="Delete track"><Trash2 size={14} /></button>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="font-mono-display text-[10px] uppercase tracking-[0.3em] text-[#00F0FF] mb-1.5 flex items-center gap-1.5">
                  <Video size={11} /> YouTube URL or ID
                </label>
                <input
                  data-testid={`track-youtube-${t.id}`}
                  value={t.youtube_id || ""}
                  onChange={(e) => handleYoutubeInput(t.id, e.target.value)}
                  placeholder="Paste full URL or 11-char ID"
                  className="w-full bg-black/40 border-b-2 border-white/15 focus:border-[#FF00E5] outline-none px-1 py-2 text-cream-soft placeholder:text-white/30 text-sm"
                />
                {t.youtube_id && (
                  <a href={`https://youtu.be/${t.youtube_id}`} target="_blank" rel="noopener noreferrer" className="mt-1 inline-block text-[10px] text-[#00F0FF] font-mono-display uppercase tracking-[0.2em] hover:underline">
                    Preview &uarr;
                  </a>
                )}
              </div>

              <div>
                <label className="block font-mono-display text-[10px] uppercase tracking-[0.3em] text-white/55 mb-1.5">Bandcamp URL</label>
                <input
                  data-testid={`track-bandcamp-${t.id}`}
                  value={t.bandcamp_url || ""}
                  onChange={(e) => updateLocal(t.id, "bandcamp_url", e.target.value)}
                  placeholder="https://...bandcamp.com/track/..."
                  className="w-full bg-black/40 border-b-2 border-white/15 focus:border-[#00F0FF] outline-none px-1 py-2 text-cream-soft placeholder:text-white/30 text-sm"
                />
              </div>

              <div>
                <label className="block font-mono-display text-[10px] uppercase tracking-[0.3em] text-white/55 mb-1.5">Tag</label>
                <input
                  data-testid={`track-tag-${t.id}`}
                  value={t.tag || ""}
                  onChange={(e) => updateLocal(t.id, "tag", e.target.value)}
                  placeholder="Single, Opener, Heavy, etc."
                  className="w-full bg-black/40 border-b-2 border-white/15 focus:border-[#FF00E5] outline-none px-1 py-2 text-cream-soft placeholder:text-white/30 text-sm"
                />
              </div>

              <div>
                <label className="block font-mono-display text-[10px] uppercase tracking-[0.3em] text-white/55 mb-1.5">Status</label>
                <select
                  data-testid={`track-status-${t.id}`}
                  value={t.status}
                  onChange={(e) => updateLocal(t.id, "status", e.target.value)}
                  className="w-full bg-black/40 border-b-2 border-white/15 focus:border-[#FF00E5] outline-none px-1 py-2 text-cream-soft text-sm"
                >
                  <option value="coming_soon" className="bg-black">Coming Soon</option>
                  <option value="released" className="bg-black">Released</option>
                  <option value="in_studio" className="bg-black">In The Studio</option>
                </select>
              </div>
            </div>

            <button
              data-testid={`track-save-${t.id}`}
              onClick={() => save(t)}
              disabled={savingId === t.id}
              className="mt-4 px-5 py-2 bg-[#00F0FF] text-black font-mono-display text-xs uppercase tracking-[0.25em] font-bold hover:bg-[#00F0FF]/90 disabled:opacity-60 flex items-center gap-2"
            >
              {savingId === t.id ? <><Loader2 size={12} className="animate-spin" /> Saving</> : <><Save size={12} /> Save Changes</>}
            </button>
          </div>
        ))}
      </div>
    </div>
  );
}



function ListTab({ endpoint, title, columns, dataKey, deleteEndpoint }) {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);

  const load = () => {
    setLoading(true);
    axios.get(`${API}/admin/${endpoint}`, { headers: authHeaders() })
      .then((r) => setRows(r.data[dataKey] || []))
      .catch(() => {})
      .finally(() => setLoading(false));
  };
  useEffect(load, [endpoint, dataKey]);

  const remove = async (id) => {
    if (!confirm("Delete this entry?")) return;
    try {
      await axios.delete(`${API}/admin/${deleteEndpoint || endpoint}/${id}`, { headers: authHeaders() });
      setRows(rows.filter((r) => r.id !== id));
      toast.success("Deleted");
    } catch {
      toast.error("Delete failed");
    }
  };

  if (loading) return <Loading />;

  return (
    <div>
      <h2 className="font-display font-black text-2xl text-cream-soft mb-5">{title} · {rows.length}</h2>
      {rows.length === 0 ? <Empty msg="Nothing here yet." /> : (
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-white/10 text-left">
                {columns.map(c => <th key={c.key} className="px-3 py-2 font-mono-display text-[10px] uppercase tracking-[0.25em] text-white/55">{c.label}</th>)}
                <th className="px-3 py-2"></th>
              </tr>
            </thead>
            <tbody>
              {rows.map((r, i) => (
                <tr key={r.id || i} className="border-b border-white/5 hover:bg-white/[0.02]">
                  {columns.map(c => <td key={c.key} className="px-3 py-2.5 text-white/80 align-top">{c.render ? c.render(r) : (r[c.key] || "—")}</td>)}
                  <td className="px-3 py-2.5 align-top">
                    {r.id && (
                      <button
                        data-testid={`delete-${endpoint}-${r.id}`}
                        onClick={() => remove(r.id)}
                        className="text-white/40 hover:text-red-400"
                        aria-label="Delete"
                      >
                        <Trash2 size={14} />
                      </button>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

function Loading() {
  return <div className="flex items-center justify-center py-12 text-white/55"><Loader2 size={16} className="animate-spin mr-2" /> Loading…</div>;
}
function Empty({ msg }) {
  return <div className="glass-card p-8 text-center text-white/55 text-sm">{msg}</div>;
}

export default function AdminPage() {
  const [auth, setAuth] = useState(Boolean(localStorage.getItem(TOKEN_KEY)));
  const [tab, setTab] = useState("shows");

  // Verify saved token on mount
  useEffect(() => {
    if (!auth) return;
    axios.get(`${API}/admin/bookings`, { headers: authHeaders() }).catch(() => {
      localStorage.removeItem(TOKEN_KEY);
      setAuth(false);
    });
  }, [auth]);

  if (!auth) return <LoginGate onLogin={() => setAuth(true)} />;

  const logout = () => {
    localStorage.removeItem(TOKEN_KEY);
    setAuth(false);
  };

  const tabs = [
    { id: "shows", label: "Shows & Slots", icon: Calendar },
    { id: "tracks", label: "Tracks & Videos", icon: Video },
    { id: "applications", label: "Band Applications", icon: Music },
    { id: "mosh", label: "Mosh Team", icon: Users },
    { id: "volunteers", label: "Crew", icon: Briefcase },
    { id: "bookings", label: "Booking Inquiries", icon: Mail },
  ];

  return (
    <div className="min-h-screen px-5 md:px-10 py-10 max-w-7xl mx-auto">
      <header className="flex items-center justify-between mb-8 pb-5 border-b border-white/10">
        <div>
          <span className="font-mono-display text-[10px] uppercase tracking-[0.35em] text-[#FF00E5]">Admin</span>
          <h1 className="font-display font-black text-3xl text-cream-soft mt-1">C.A.F. CONTROL</h1>
        </div>
        <button data-testid="admin-logout" onClick={logout} className="px-3 py-2 border border-white/20 text-white/70 hover:border-[#FF00E5] hover:text-[#FF00E5] text-xs flex items-center gap-2 font-mono-display uppercase tracking-[0.2em]"><LogOut size={12} /> Sign out</button>
      </header>

      <nav className="flex flex-wrap gap-2 mb-8">
        {tabs.map(t => (
          <button
            key={t.id}
            data-testid={`admin-tab-${t.id}`}
            onClick={() => setTab(t.id)}
            className={`px-4 py-2 border font-mono-display text-[11px] uppercase tracking-[0.2em] transition-all flex items-center gap-2 ${tab === t.id ? "border-[#FF00E5] text-[#FF00E5] glow-magenta" : "border-white/15 text-white/55 hover:border-white/40"}`}
          >
            <t.icon size={12} /> {t.label}
          </button>
        ))}
      </nav>

      {tab === "shows" && <ShowsTab />}
      {tab === "tracks" && <TracksTab />}
      {tab === "applications" && (
        <ListTab endpoint="applications" deleteEndpoint="applications" dataKey="applications" title="Band & Vendor Applications" columns={[
          { key: "created_at", label: "Date", render: (r) => (r.created_at || "").slice(0, 10) },
          { key: "band_name", label: "Band/Vendor" },
          { key: "desired_slot_type", label: "Slot", render: (r) => SLOT_LABEL[r.desired_slot_type] || r.desired_slot_type },
          { key: "email", label: "Contact", render: (r) => <a href={`mailto:${r.email}`} className="text-[#00F0FF] hover:underline">{r.email}</a> },
          { key: "epk_url", label: "EPK", render: (r) => r.epk_url ? <a href={r.epk_url} target="_blank" rel="noopener noreferrer" className="text-[#00F0FF] hover:underline">link</a> : "—" },
          { key: "message", label: "Message", render: (r) => <div className="max-w-xs text-xs text-white/70 line-clamp-3">{r.message}</div> },
        ]} />
      )}
      {tab === "mosh" && (
        <ListTab endpoint="mosh-team" deleteEndpoint="mosh-team" dataKey="members" title="Mosh Team Members" columns={[
          { key: "created_at", label: "Joined", render: (r) => (r.created_at || "").slice(0, 10) },
          { key: "email", label: "Email" },
          { key: "tier", label: "Tier", render: (r) => <span className={r.tier === "vip" ? "text-[#FF00E5]" : "text-[#00F0FF]"}>{r.tier?.toUpperCase()}</span> },
          { key: "instagram", label: "Instagram" },
        ]} />
      )}
      {tab === "volunteers" && (
        <ListTab endpoint="volunteers" deleteEndpoint="volunteers" dataKey="volunteers" title="Crew Applications" columns={[
          { key: "created_at", label: "Date", render: (r) => (r.created_at || "").slice(0, 10) },
          { key: "name", label: "Name" },
          { key: "email", label: "Email" },
          { key: "location", label: "City" },
          { key: "roles", label: "Roles", render: (r) => (r.roles || []).join(", ") },
          { key: "why", label: "Why", render: (r) => <div className="max-w-xs text-xs text-white/70 line-clamp-3">{r.why}</div> },
        ]} />
      )}
      {tab === "bookings" && (
        <ListTab endpoint="bookings" deleteEndpoint="bookings" dataKey="bookings" title="Booking Inquiries" columns={[
          { key: "created_at", label: "Date", render: (r) => (r.created_at || "").slice(0, 10) },
          { key: "inquiry_type", label: "Type" },
          { key: "name", label: "Name" },
          { key: "email", label: "Email" },
          { key: "subject", label: "Subject" },
          { key: "message", label: "Message", render: (r) => <div className="max-w-xs text-xs text-white/70 line-clamp-3">{r.message}</div> },
        ]} />
      )}
    </div>
  );
}
